import React, { useCallback, useMemo, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import i18n from 'i18n-js';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import {
  updateMachine,
  deleteMachine,
  switchConnection,
  setMachines,
  setSelectedMachine,
  selectMachines,
  selectSelectedItem,
} from '../../../../../../../reducers/machines';
import { useGetMachineById } from '@api/modules/machines/hooks';
import {
  Alert,
  navigateBack,
  getStateParams,
  Popup,
  navigateTo,
  isPhone,
  injectConditionalArrayItems,
  subStatusName,
} from '../../../../../../../helpers';
import { scale } from '@components/frame';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import {
  convertTimeToString,
  getMachineCountingMethodLabel,
  buildMachinePropertiesMap,
  buildCredentialsData,
  buildFilterExceptionList,
} from '@components/settings/v2/structure/machines/machineDetails/helpers';
import { WithColor } from '@styles/colors';
import AddMachineModal from '@components/settings/v2/structure/machines/machines/addMachine';
import { Machine } from '@interfaces/machines';

interface UseManageMachineDetailsParams extends WithColor {}

export const useManageMachineDetails = ({ C }: UseManageMachineDetailsParams) => {
  const route = useRoute();
  const dispatch = useAppDispatch();
  const machines = useSelector(selectMachines);
  const selectedItem = useSelector(selectSelectedItem);

  const [connectionLoading, setConnectionLoading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showEditField, setShowEditField] = useState(false);
  const [editFieldConfig, setEditFieldConfig] = useState<Record<string, any>>({});

  const id = getStateParams(route).id;
  const { machine: inspectedMachine, refetch } = useGetMachineById({ id });
  const machine: Machine = inspectedMachine ?? {};
  const loaded = inspectedMachine != null;

  const { machineId, machineBrand = {}, machineConnection = {}, machineStatus = {}, fullName, shortName, operations } = machine;

  const isConnected = machineStatus.isConnected && machineConnection.switchedOn;

  const toggleEditField = () => {
    setShowEditField(!showEditField);
  };

  const removeMachine = () => {
    Alert({
      title: i18n.t('Are you sure?'),
      actions: [
        {
          text: i18n.t('No'),
          onPress: () => {},
          style: 'primary',
        },
        {
          text: i18n.t('Yes'),
          onPress: async () => {
            setRemoving(true);
            await dispatch(deleteMachine(machine.id));
            navigateBack();
            setRemoving(false);
          },
        },
      ],
    });
  };

  const triggerConnection = async (value: boolean) => {
    setConnectionLoading(true);
    await dispatch(
      switchConnection({
        machineId: machine.id,
        switchedOn: value,
      })
    );
    await refetch();
    setConnectionLoading(false);

    const updatedMachine = inspectedMachine ?? machine;
    dispatch(setMachines(machines.map((m: any) => (m.id === machine.id ? updatedMachine : m))));
    if (selectedItem?.id === machine?.id) {
      dispatch(setSelectedMachine(updatedMachine));
    }
  };

  const triggerEditName = (isShortName = false) => {
    const { key, name } = !isShortName ? { key: 'fullName', name: 'Full Name' } : { key: 'shortName', name: 'Short Name' };

    setEditFieldConfig({
      header: 'Machine Name',
      initData: { fullName, shortName },
      onSubmit: async (data: Record<string, any>, onClose: () => void) => {
        const shortNameData = data.fullName.length < 20 ? data.fullName : data.shortName;
        const updateName = key === 'fullName' ? { shortName: shortNameData, [key]: data[key] } : { [key]: data[key] };

        const result = { ...machine, ...updateName };

        await dispatch(updateMachine({ machine: result }, result));
        await refetch();
        onClose();
      },
      inputs: [
        {
          key,
          name,
          topName: true,
          noArrow: true,
          isLable: false,
          placeholder: ' ',
          maxLength: isShortName ? 20 : 100,
        },
      ],
      round: true,
    });
    toggleEditField();
  };

  const handleNavigateToOperations = () => {
    navigateTo('Settings', {
      screen: 'Machines',
      params: {
        screen: 'MachineAssignedOperations',
        params: { id },
      },
    });
  };

  const triggerEditConnectionMethod = () => {
    Popup({
      Component: (p: any) =>
        React.createElement(AddMachineModal, {
          ...p,
          initialMode: 'ADD_MACHINE_IP',
          initialData: {
            endpoint: machineConnection.endpoint,
            machineId,
            fullName,
            shortName,
          },
          isReOnboarding: true,
        }),
      width: scale(580),
    });
  };

  const backButtonAction = () => {
    navigateTo('Settings', {
      screen: 'Machines',
      params: {
        screen: 'MachinesScreen',
      },
    });
  };

  const machineProperties = useMemo(() => buildMachinePropertiesMap(machine?.machineProperties), [machine?.machineProperties]);

  const credentialsData = useMemo(
    () => buildCredentialsData(machineConnection?.machineCredentials, machineProperties.folder),
    [machineConnection?.machineCredentials, machineConnection.password, machineConnection.user, machineProperties.folder]
  );

  const filterExceptionList = useMemo(
    () => buildFilterExceptionList(machineConnection?.connectionMethod?.code),
    [machineConnection?.connectionMethod?.code]
  );

  const initialSectionData = useMemo(
    () => [
      {
        value: machineConnection.endpoint,
        name: i18n.t('Endpoint'),
        noArrow: false,
        bottom: machineConnection.connectionMethod?.name,
        bottomSide: 'right',
        align: 'left',
        onClick: triggerEditConnectionMethod,
        shouldApplyFlex2: true,
      },
    ],
    [machineConnection.connectionMethod?.name, machineConnection.endpoint, machineConnection.ipAddress]
  );

  const machineReference = machine?.machineReferences?.[0];
  const hasMachineImnooIntegration = machineReference?.sourceSystem === 'Imnoo';
  const machineCountingMethod = machine?.machineCountingMethod;

  const sectionsData = useMemo(() => {
    return [
      {
        description: i18n.t('We recommend a short machine name with 20 or less characters.'),
        details: [
          {
            value: fullName,
            name: i18n.t('Machine Name'),
            onClick: () => triggerEditName(),
            defaultValue: !isPhone,
          },
          {
            value: shortName,
            name: i18n.t('Machine Short Name'),
            onClick: () => triggerEditName(true),
            defaultValue: !isPhone,
          },
        ],
      },
      {
        details: [
          {
            value: i18n.t(subStatusName[machineStatus.subStatus]),
            name: i18n.t('Machine Status'),
            noArrow: true,
            defaultValue: !isPhone,
          },
          {
            value: convertTimeToString(machineStatus?.lastUpdatedOn),
            name: i18n.t('Last Event Received'),
            noArrow: true,
            defaultValue: !isPhone,
          },
          {
            value: machineStatus.ncFile,
            name: i18n.t('Selected NC File'),
            noArrow: true,
            defaultValue: !isPhone,
          },
        ],
      },
      {
        description: i18n.t('This counting method was automatically selected by Smart Factory.'),
        details: [
          {
            value: operations?.length,
            name: i18n.t('Operations links'),
            noArrow: false,
            testID: TEST_IDS.machineDetails.operationsLinksLine,
            onClick: handleNavigateToOperations,
          },
          ...injectConditionalArrayItems(hasMachineImnooIntegration, {
            value: machineReference?.name,
            name: i18n.t('Imnoo Machine'),
            noArrow: false,
            onClick: () =>
              navigateTo('Settings', {
                screen: 'Machines',
                params: {
                  screen: 'MachinesScreen',
                  params: {
                    screen: 'MachinesImnoo',
                  },
                },
              }),
          }),
          {
            value: getMachineCountingMethodLabel(machineCountingMethod),
            name: i18n.t('Count Settings'),
            testID: TEST_IDS.machineDetails.countSettingsLine,
            noArrow: false,
            onClick: () =>
              navigateTo('Settings', {
                screen: 'Machines',
                params: {
                  screen: 'MachineCountSettings',
                  params: { id: machineId },
                },
              }),
          },
        ],
      },
      ...credentialsData,
      ...filterExceptionList,
      {
        details: [
          {
            value: machineConnection.macAddress,
            name: i18n.t('MAC Address'),
            noArrow: true,
            defaultValue: !isPhone,
          },
          {
            value: machineConnection.port,
            name: i18n.t('Communication Port'),
            noArrow: true,
            defaultValue: !isPhone,
          },
          {
            value: machineBrand?.name,
            name: i18n.t('Machine Brand'),
            defaultValue: !isPhone,
            noArrow: true,
          },
          {
            value: machineProperties.machineModel,
            name: i18n.t('Machine Model'),
            defaultValue: !isPhone,
            noArrow: true,
          },
          {
            value: machineProperties.controlMachineType,
            name: i18n.t('Machine Type'),
            defaultValue: !isPhone,
            noArrow: true,
          },
          {
            value: machineProperties.controlSerialNumber,
            name: i18n.t('Machine Serial'),
            defaultValue: !isPhone,
            noArrow: true,
          },
          {
            value: machineProperties.controlBrand,
            name: i18n.t('Control Brand'),
            noArrow: true,
            defaultValue: !isPhone,
          },
          {
            value: machineProperties.controlType,
            name: i18n.t('Control Type'),
            noArrow: true,
            defaultValue: !isPhone,
          },
          {
            value: machineProperties.controlSoftwareVersion,
            name: i18n.t('Control Software Version'),
            noArrow: true,
            defaultValue: !isPhone,
          },
          {
            value: machineProperties.controlSerialNumber,
            name: i18n.t('Control Serial'),
            noArrow: true,
            defaultValue: !isPhone,
          },
        ],
      },
      {
        details: [
          {
            value: convertTimeToString(machine.createdOn),
            name: i18n.t('Created on'),
            noArrow: true,
            defaultValue: !isPhone,
          },
          {
            value: machine.createdBy,
            name: i18n.t('Created by'),
            noArrow: true,
            defaultValue: !isPhone,
          },
          {
            value: convertTimeToString(machine.modifiedOn),
            name: i18n.t('Modified on'),
            noArrow: true,
            defaultValue: !isPhone,
          },
          {
            value: machine.modifiedBy,
            name: i18n.t('Modified by'),
            noArrow: true,
            defaultValue: !isPhone,
          },
        ],
      },
    ];
  }, [credentialsData, machine, machineCountingMethod]);

  return {
    id,
    machine,
    loaded,
    machineId,
    machineBrand,
    machineConnection,
    machineStatus,
    fullName,
    shortName,
    isConnected,
    connectionLoading,
    removing,
    showEditField,
    editFieldConfig,
    machineProperties,
    initialSectionData,
    sectionsData,
    removeMachine,
    triggerConnection,
    triggerEditConnectionMethod,
    toggleEditField,
    backButtonAction,
  };
};
